import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useSetAtom } from 'jotai';

import { fetchWhoAmI, signIn } from '@/api/v4/auth.api';
import { BaseExceptions, isBaseError } from '@/api/v4/base-fetch';
import { isInactiveAccountError } from '@/helpers/is-inactive-account-error';
import { isNoActiveSubscriptionError } from '@/helpers/is-no-active-subscription-error';
import { clearUserData } from '@/helpers/logout-utils';
import { identifyUser, trackSignIn } from '@/lib/mixpanel/mixpanel';
import { useAppRedirect } from '@/routes/hooks/use-app-redirect';
import { useNavigateTo } from '@/routes/hooks/use-navigate-to';
import { notify } from '@/ui/snackbar/notify';
import { currentUserQuery } from '@/user/use-current-user.query';

import { jwtTokenState } from '../auth.state';

export const useSignIn = () => {
  const navigateTo = useNavigateTo();
  const { redirectToIntended } = useAppRedirect();

  const { t } = useTranslation('auth');
  const queryClient = useQueryClient();
  const setToken = useSetAtom(jwtTokenState);

  const mutation = useMutation({
    mutationFn: signIn,
    onSuccess: async data => {
      setToken('Bearer ' + data.token);

      const query = currentUserQuery();

      try {
        const user = await fetchWhoAmI();
        queryClient.setQueryData(query.queryKey, user);

        identifyUser({
          email: user.email,
          firstName: user.firstName,
          id: user.id,
          lastName: user.lastName,
          role: user.role,
        });

        trackSignIn({
          login_method: 'descope',
          success: true,
          user_id: user.id,
        });

        redirectToIntended({ fallbackPath: '/home' });
      } catch (error) {
        if (isInactiveAccountError(error)) {
          // Clear user data for inactive accounts using shared utility
          clearUserData({ queryClient, setToken });
          navigateTo.signIn();
          return;
        }
        if (isNoActiveSubscriptionError(error)) {
          navigateTo.accountDisabled();
          return;
        }
        navigateTo.signIn();
      }
    },
    onError: error => {
      trackSignIn({
        login_method: 'descope',
        success: false,
      });

      if (isBaseError(error)) {
        if (error.response.status === BaseExceptions.UNAUTHORIZED) {
          notify({ message: t`loginErrorNoUser` });
          return;
        }
      }
      notify({ message: t`loginError` });
    },
  });

  return {
    handleSignIn: mutation.mutateAsync,
  };
};
